import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';

interface UseUnreadNotificationCountResult {
  unreadCount: number;
  loading: boolean;
  error: string | null;
}

/**
 * Hook for real-time unread notification count
 * No caching - always live data from Firebase
 */
export const useUnreadNotificationCount = (): UseUnreadNotificationCountResult => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { currentUser, authState } = useAuth();

  useEffect(() => {
    // Don't setup listener if user is not authenticated or still loading
    if (!currentUser || authState.loading) {
      setLoading(authState.loading);
      setError(null);
      setUnreadCount(0);
      return;
    }

    // Setup real-time listener for user's notification document
    const userNotificationDocRef = doc(db, 'notifications', currentUser.uid);
    
    const unsubscribe = onSnapshot(
      userNotificationDocRef,
      (doc) => {
        setLoading(false);
        setError(null);
        
        if (doc.exists()) {
          const data = doc.data();
          const unreadCount = data?.unread || 0;
          setUnreadCount(unreadCount);
        } else {
          // Document doesn't exist yet - no notifications
          setUnreadCount(0);
        }
      },
      (err) => {
        console.error('Error listening to unread notification count:', err);
        setLoading(false);
        
        // Don't show error for permission errors when document doesn't exist yet
        if (err.code === 'permission-denied' || err.message.includes('not-found')) {
          setUnreadCount(0);
          setError(null);
        } else {
          setError(err.message);
        }
      }
    );

    // Cleanup listener on unmount or user change
    return () => {
      unsubscribe();
    };
  }, [currentUser, authState.loading]);

  return {
    unreadCount,
    loading,
    error
  };
};